import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle } from "react-icons/fa";

import { formatIdentifier } from "@/utils/formatIdentifier";
import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { getCitizenScoreStatusDescriptionIndicatorC4 } from "@/utils/indicatorsEsf";
import { DetailedCitizenAttendancesIndicatorC4Data } from "@/services/esus/indicator/esf/c4/getDetailedCitizensAttendancesIndicatorC4";
import { Column } from "@/@types/generics/Column";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type IndicatorEsfC4AttendanceTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	healthTeam: Column;
	microarea: Column;
	lastAttendance: Column;
	lastBloodPressureMeasurement: Column;
	lastWeightAndHeightMetricsRegister: Column;
	lastGlycatedHemoglobin: Column;
	lastFootAssessment: Column;
};

type IndicatorEsfC4CitizensAttendancesTableProps = {
	citizensWithAttendances: DetailedCitizenAttendancesIndicatorC4Data[];
	isLoading: boolean;
	openCitizenHistory: (citizenWithAttendances: DetailedCitizenAttendancesIndicatorC4Data) => void;
};

export function IndicatorEsfC4CitizensAttendancesTable({
	citizensWithAttendances,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsfC4CitizensAttendancesTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC4AttendanceTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		lastAttendance: { title: "Última Consulta", visible: true },
		lastBloodPressureMeasurement: { title: "Aferição PA", visible: true },
		lastWeightAndHeightMetricsRegister: { title: "Peso e Altura", visible: true },
		lastGlycatedHemoglobin: { title: "Hemoglob. Glicada", visible: true },
		lastFootAssessment: { title: "Exame dos pés", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.lastAttendance.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.lastAttendance.title}
							<CardOverlayTrigger
								header="Última Consulta"
								message="Última Consulta realizada nos últimos 6 meses"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.lastBloodPressureMeasurement.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.lastBloodPressureMeasurement.title}
							<CardOverlayTrigger
								header="Aferição de PA"
								message="Última aferição de PA nos últimos 6 meses"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th
						className={`px-2 ${
							!columns.lastWeightAndHeightMetricsRegister.visible && "d-none"
						}`}
					>
						<div className="d-flex flex-column">
							{columns.lastWeightAndHeightMetricsRegister.title}
							<CardOverlayTrigger
								header="Peso e altura"
								message="Último registro de peso e altura registrado nos últimos 12 meses"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.lastGlycatedHemoglobin.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.lastGlycatedHemoglobin.title}
							<CardOverlayTrigger
								header="Hemoglobina glicada"
								message="Último registro hemoglobina glicada, solicitada ou avaliada, nos últimos 12 meses"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.lastFootAssessment.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.lastFootAssessment.title}
							<CardOverlayTrigger
								header="Exame dos pés"
								message="Último registro de avaliação dos pés realizado nos últimos 12 meses"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC4AttendanceTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC4AttendanceTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC4AttendanceTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensWithAttendances.length > 0
					? citizensWithAttendances.map((citizenWithAttendances, index) => (
							<tr key={`citizen-attendances-table-row-${index + 1}`}>
								<td className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithAttendances.fullName}</div>
										<div className="text-muted">
											{citizenWithAttendances.birthdate
												? dayjs(citizenWithAttendances.birthdate).format(
														"DD/MM/YYYY"
												  )
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.citizenAge.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.birthdate
										? getAge(citizenWithAttendances.birthdate)
										: ""}
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.document.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.cpf
										? formatIdentifier(citizenWithAttendances.cpf)
										: citizenWithAttendances.cns ?? ""}
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithAttendances.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{citizenWithAttendances.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.area ?? "FA"}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.lastAttendance.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>
											{getCitizenScoreStatusDescriptionIndicatorC4(
												citizenWithAttendances.lastAttendanceStatus
											)}
										</div>
										<div className="text-muted">
											{citizenWithAttendances.lastAttendance?.date
												? dayjs(
														citizenWithAttendances.lastAttendance.date
												  ).format("DD/MM/YYYY")
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center px-2 ${
										!columns.lastBloodPressureMeasurement.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>
											{getCitizenScoreStatusDescriptionIndicatorC4(
												citizenWithAttendances.lastBloodPressureMeasurementStatus
											)}
										</div>
										<div className="text-muted">
											{citizenWithAttendances.bloodPressureMeasurements?.at(0)
												?.date
												? dayjs(
														citizenWithAttendances.bloodPressureMeasurements?.at(
															0
														)?.date
												  ).format("DD/MM/YYYY")
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center px-2 ${
										!columns.lastWeightAndHeightMetricsRegister.visible &&
										"d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>
											{getCitizenScoreStatusDescriptionIndicatorC4(
												citizenWithAttendances.lastWeightAndHeightMetricsRegisterStatus
											)}
										</div>
										<div className="text-muted">
											{citizenWithAttendances.weightAndHeightMetricsRegisters?.at(
												0
											)?.date
												? dayjs(
														citizenWithAttendances.weightAndHeightMetricsRegisters?.at(
															0
														)?.date
												  ).format("DD/MM/YYYY")
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center px-2 ${
										!columns.lastGlycatedHemoglobin.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>
											{getCitizenScoreStatusDescriptionIndicatorC4(
												citizenWithAttendances.lastGlycatedHemoglobinStatus
											)}
										</div>
										<div className="text-muted">
											{citizenWithAttendances.glycatedHemoglobinAttendanceProceduresReqEval?.at(
												0
											)?.date
												? dayjs(
														citizenWithAttendances.glycatedHemoglobinAttendanceProceduresReqEval?.at(
															0
														)?.date
												  ).format("DD/MM/YYYY")
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center px-2 ${
										!columns.lastFootAssessment.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>
											{getCitizenScoreStatusDescriptionIndicatorC4(
												citizenWithAttendances.lastFootAssessmentStatus
											)}
										</div>
										<div className="text-muted">
											{citizenWithAttendances.footAssessmentAttendanceProceduresReqEval?.at(
												0
											)?.date
												? dayjs(
														citizenWithAttendances.footAssessmentAttendanceProceduresReqEval?.at(
															0
														)?.date
												  ).format("DD/MM/YYYY")
												: ""}
										</div>
									</div>
								</td>

								<td className="text-center">
									<Button
										className="bg-primary py-1 px-3"
										onClick={() => openCitizenHistory(citizenWithAttendances)}
										title="Histórico do cidadão"
									>
										<FaEye className="fs-4" />
									</Button>
								</td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
